import { useBulkPutExtractionsApi, usePutExtractionApi } from 'api/extraction'
import { Flex } from 'components/shared/flex'
import { Attribute, DateField, InputField, ReagentItemSelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Extraction, ExtractionForm } from 'entities/extraction'
import { useContext } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm, usePrevious } from 'utils/hooks'
import lodash from 'lodash'
import { ApiError } from 'utils/network/api_hooks'
import { TableCell } from 'components/shared/widget'

type EditExtractionFormProps = {
    extraction: Extraction
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditExtractionForm = (props: EditExtractionFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutExtractionApi()
    const form = useForm<ExtractionForm>(props.extraction)

    useEffectSkipFirst(() => {
        form.set(props.extraction)
    }, [props.extraction])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Extractionの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {form.object.id && (
                    <>
                        <h3 style={{ marginBottom: 10 }}>ExtractionID: {props.extraction.code}</h3>
                        <div style={{ marginBottom: 20 }}>
                            <Flex>
                                <DateField required apiError={api.apiError} form={form} attr="date" label="Date" />
                            </Flex>
                            <Flex>
                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="Purification lot"
                                    attr="purificationReagentItemId"
                                    reagentKind="purification"
                                />
                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="Spike-in"
                                    attr="rnaSpikeInReagentItemId"
                                    reagentKind="rna_spike_in"
                                />
                            </Flex>
                            <Flex>
                                <ReagentItemSelectField apiError={api.apiError} form={form} label="Ethanol" attr="ethanolReagentItemId" reagentKind="ethanol" />
                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="Free water"
                                    attr="rnaFreeWaterReagentItemId"
                                    reagentKind="free_water"
                                />
                            </Flex>
                            <InputField apiError={api.apiError} form={form} label="ExpQbit(ng/μL)" attr="expQbit" type="number" step={0.01} min={0} />
                            <Flex>
                                <InputField apiError={api.apiError} form={form} attr="errorNote" as="textarea" label="エラー" />
                            </Flex>
                            <Flex>
                                <InputField apiError={api.apiError} form={form} attr="note" as="textarea" label="Note" />
                            </Flex>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

type BulkEditExtractionFormProps = {
    extractions: Extraction[]
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const BulkEditExtractionForm = (props: BulkEditExtractionFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = useBulkPutExtractionsApi()
    const form = useForm<ExtractionForm[]>(props.extractions)
    const allInputForm = useForm<ExtractionForm>({})
    const previousAllInputForm = usePrevious<ExtractionForm>(allInputForm.object)

    useEffectSkipFirst(() => {
        form.set(props.extractions)
    }, [props.extractions])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    const bulkInput = (attr: keyof ExtractionForm) => {
        const copy = [...form.object]
        copy.forEach((extraction: ExtractionForm) => {
            const value: any = allInputForm.object[attr] as any
            extraction[attr] = value
        })
        form.set(copy)
    }

    useEffectSkipFirst(() => {
        const diff: ExtractionForm = lodash.omitBy(allInputForm.object, (v, k) => previousAllInputForm[k as keyof ExtractionForm] === v)
        Object.keys(diff).forEach((k: string) => {
            bulkInput(k as keyof ExtractionForm)
        })
    }, [allInputForm.object])

    const handleClose = () => {
        api.clearApiError()
        props.onClose()
    }

    const formRow = <T extends unknown>(form: Form<T>, apiError: ApiError, extraction?: Extraction, index?: number): JSX.Element => {
        const getAttr = (attr: string): Attribute<T> => {
            if (typeof index === 'number') {
                return [index, attr]
            } else {
                return attr as keyof T
            }
        }

        return (
            <>
                <th className="sticky-table-cell" style={{ backgroundColor: extraction?.code ? 'white' : '#eee' }}>
                    {extraction?.code ? extraction.code : '一括入力'}
                </th>
                <td>
                    <DateField required apiError={apiError} form={form} attr={getAttr('date')} index={index} />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('purificationReagentItemId')} reagentKind="purification" />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('rnaSpikeInReagentItemId')} reagentKind="rna_spike_in" />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('ethanolReagentItemId')} reagentKind="ethanol" />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('rnaFreeWaterReagentItemId')} reagentKind="free_water" />
                </td>
                <td>
                    <InputField apiError={apiError} form={form} attr={getAttr('expQbit')} type="number" step={0.01} min={0} index={index} />
                </td>
                <td>
                    <InputField apiError={apiError} form={form} attr={getAttr('errorNote')} index={index} />
                </td>
                <td>
                    <InputField apiError={apiError} form={form} attr={getAttr('note')} index={index} />
                </td>
            </>
        )
    }

    return (
        <Modal show={props.show} onHide={handleClose} size="xl" fullscreen>
            <Modal.Header closeButton>
                <Modal.Title>Extractionの一括編集</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                {form.object.length > 0 && (
                    <div>
                        <Table className="sticky-table">
                            <thead>
                                <tr>
                                    <TableCell className="sticky-table-cell" th minWidth={120}>
                                        ExtractionID
                                    </TableCell>
                                    <TableCell th minWidth={140}>
                                        Date
                                    </TableCell>
                                    <TableCell th minWidth={150}>
                                        Purification lot
                                    </TableCell>
                                    <TableCell th minWidth={150}>
                                        Spike-in
                                    </TableCell>
                                    <TableCell th minWidth={150}>
                                        Ethanol
                                    </TableCell>
                                    <TableCell th minWidth={100}>
                                        Free water
                                    </TableCell>
                                    <TableCell th minWidth={100}>
                                        ExpQbit(ng/μL)
                                    </TableCell>
                                    <TableCell th minWidth={300}>
                                        エラー
                                    </TableCell>
                                    <TableCell th minWidth={300}>
                                        Note
                                    </TableCell>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    className="vertical-align-middle-row"
                                    style={{
                                        borderBottomStyle: 'double',
                                        borderBottomWidth: '6px',
                                        background: '#eee',
                                    }}
                                >
                                    {formRow(allInputForm, api.apiError)}
                                </tr>
                                {props.extractions.map((extraction: Extraction, index: number) => {
                                    return (
                                        <tr key={`bulk-update-extraction-${extraction.id}`} className="vertical-align-middle-row">
                                            {formRow(form, api.apiError, extraction, index)}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                        <Flex></Flex>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
